import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Clock, MapPin, Phone, MessageCircle, Music4, Instagram } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = { name: "", phone: "", date: "", guests: "", message: "" };

const inputCls =
  "w-full bg-[#141418] border border-[#2A2A34] rounded-lg px-4 py-3 text-sm text-[#F5F2EB] placeholder:text-stone-500 focus:border-[#F49D37] outline-none transition-colors";

export default function ContactSection() {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Richiesta inviata! Ti ricontattiamo al più presto.");
      setForm(EMPTY);
    } catch {
      toast.error("Qualcosa è andato storto. Riprova o chiamaci.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contatti" data-testid="contact-section" className="py-24 sm:py-32 bg-[#141418] grain">
      <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-xs font-mono2 tracking-[0.25em] uppercase text-[#F49D37]"
        >
          Contatti & Orari
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="font-display uppercase text-4xl sm:text-5xl lg:text-6xl leading-[0.95] mt-4 mb-12"
        >
          Ti aspettiamo <span className="text-[#D34E36]">in piazza</span>
        </motion.h2>

        <div className="grid lg:grid-cols-2 gap-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="space-y-6"
          >
            <div
              data-testid="opening-hours-card"
              className="bg-[#1C1C22] border border-[#2A2A34] rounded-2xl p-7"
            >
              <h3 className="font-display uppercase text-2xl tracking-wide flex items-center gap-3">
                <Clock size={20} className="text-[#F49D37]" /> Orari
              </h3>
              <ul className="mt-5 space-y-3 text-sm">
                <li className="flex justify-between gap-4">
                  <span className="text-stone-300">Lunedì · Mercoledì – Domenica</span>
                  <span className="font-mono2 text-[#F49D37]">19:00 – 02:00</span>
                </li>
                <li className="flex justify-between gap-4">
                  <span className="text-stone-300">Martedì</span>
                  <span className="font-mono2 text-[#D34E36] uppercase text-xs tracking-widest pt-0.5">Chiuso</span>
                </li>
                <li className="flex justify-between gap-4 items-center pt-3 border-t border-[#2A2A34]">
                  <span className="text-stone-300 flex items-center gap-2">
                    <Music4 size={14} className="text-[#F49D37]" /> Weekend
                  </span>
                  <span className="font-mono2 text-stone-300 uppercase text-xs tracking-widest">DJ Set</span>
                </li>
              </ul>
              <div className="flex items-start gap-2 mt-6 text-sm text-stone-400">
                <MapPin size={16} className="text-[#D34E36] shrink-0 mt-0.5" />
                Piazza XX Settembre 22/23, 14049 Nizza Monferrato (AT)
              </div>
              <div className="flex flex-wrap gap-3 mt-6">
                <a
                  href="tel:+393937347177"
                  data-testid="phone-contact-button"
                  className="flex items-center gap-2 bg-[#D34E36] hover:bg-[#F49D37] hover:text-black text-[#F5F2EB] text-[11px] font-bold uppercase tracking-widest px-5 py-3 rounded-full transition-colors"
                >
                  <Phone size={14} /> +39 393 7347177
                </a>
                <a
                  href="https://wa.me/393937347177"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="whatsapp-contact-button"
                  className="flex items-center gap-2 border border-white/25 hover:border-[#F49D37] hover:text-[#F49D37] text-[#F5F2EB] text-[11px] font-bold uppercase tracking-widest px-5 py-3 rounded-full transition-colors"
                >
                  <MessageCircle size={14} /> WhatsApp
                </a>
                <a
                  href="https://www.instagram.com/lateresina_nizza_monferrato"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="instagram-contact-button"
                  className="flex items-center gap-2 border border-white/25 hover:border-[#F49D37] hover:text-[#F49D37] text-[#F5F2EB] text-[11px] font-bold uppercase tracking-widest px-5 py-3 rounded-full transition-colors"
                >
                  <Instagram size={14} /> Instagram
                </a>
              </div>
            </div>

            <form
              onSubmit={submit}
              data-testid="booking-form"
              className="bg-[#1C1C22] border border-[#2A2A34] rounded-2xl p-7"
            >
              <h3 className="font-display uppercase text-2xl tracking-wide">Prenota un tavolo</h3>
              <div className="grid sm:grid-cols-2 gap-4 mt-6">
                <input
                  required
                  value={form.name}
                  onChange={set("name")}
                  placeholder="Nome *"
                  data-testid="contact-name-input"
                  className={inputCls}
                />
                <input
                  value={form.phone}
                  onChange={set("phone")}
                  placeholder="Telefono"
                  type="tel"
                  data-testid="contact-phone-input"
                  className={inputCls}
                />
                <input
                  value={form.date}
                  onChange={set("date")}
                  type="date"
                  data-testid="contact-date-input"
                  className={`${inputCls} [color-scheme:dark]`}
                />
                <input
                  value={form.guests}
                  onChange={set("guests")}
                  placeholder="Quanti siete?"
                  type="number"
                  min="1"
                  data-testid="contact-guests-input"
                  className={inputCls}
                />
              </div>
              <textarea
                value={form.message}
                onChange={set("message")}
                placeholder="Messaggio (opzionale)"
                rows={3}
                data-testid="contact-message-input"
                className={`${inputCls} mt-4 resize-none`}
              />
              <button
                type="submit"
                disabled={sending}
                data-testid="contact-submit-button"
                className="mt-5 w-full bg-[#D34E36] hover:bg-[#F49D37] hover:text-black disabled:opacity-50 text-[#F5F2EB] font-bold uppercase tracking-widest text-xs px-7 py-3.5 rounded-full transition-colors"
              >
                {sending ? "Invio in corso..." : "Invia richiesta"}
              </button>
            </form>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="min-h-[420px] rounded-2xl overflow-hidden border border-[#2A2A34]"
          >
            <iframe
              title="Mappa — Lateresina, Piazza XX Settembre 22/23, Nizza Monferrato"
              data-testid="google-maps-iframe"
              src="https://www.google.com/maps?q=Piazza%20XX%20Settembre%2022%2C%2014049%20Nizza%20Monferrato%20AT%2C%20Italia&output=embed"
              className="w-full h-full min-h-[420px] map-frame"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            />
          </motion.div>
        </div>
      </div>
    </section>
  );
}
